import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ResponsiveLayoutProvider } from './contexts/ResponsiveLayoutContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import AuthLayout from './components/layout/AuthLayout';
import { TASK_ROUTE, WORKSPACE_ROUTE } from './lib/routes';
import LoadingIndicator from './components/common/LoadingIndicator';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallbackPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ImpersonationPage = lazy(() => import('./pages/ImpersonationPage'));
// Lazy for its payload, not for its position: see providers/DatePickerProvider.
const DatePickerProvider = lazy(() => import('./providers/DatePickerProvider'));

// Exported for the tests, and only for them — nothing else imports these two.
// The alternative was to reach them by rendering App itself, and that does not
// work here: the pages are lazy(), and a dynamic import is not intercepted by
// the module mocks the test would need. So the guard that decides whether a
// signed-out visitor reaches the dashboard was the one piece of routing no test
// could touch.
export const SuspenseFallback = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <LoadingIndicator />
  </Box>
);

export const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <SuspenseFallback />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

/**
 * The authenticated workspace, mounted by both of its routes.
 *
 * Written once rather than twice in the route table: two copies of a guarded
 * subtree is how one of them eventually loses the guard.
 */
export const Workspace: React.FC = () => (
  <PrivateRoute>
    <DatePickerProvider>
      <ErrorBoundary>
        <DashboardPage />
      </ErrorBoundary>
    </DatePickerProvider>
  </PrivateRoute>
);

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <CssBaseline />
        <BrowserRouter>
          <AuthProvider>
            <ResponsiveLayoutProvider>
              <WebSocketProvider>
                <Suspense fallback={<SuspenseFallback />}>
                  <Routes>
                    <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
                    <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
                    <Route path="/oauth/callback" element={<AuthLayout><OAuthCallbackPage /></AuthLayout>} />
                    <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
                    <Route path="/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
                    <Route path="/verify-email" element={<AuthLayout><VerifyEmailPage /></AuthLayout>} />
                    <Route path="/impersonate" element={<AuthLayout><ImpersonationPage /></AuthLayout>} />
                    {/* Two paths, one workspace. The task id is a segment rather
                        than component state so a selection can be linked,
                        reloaded and stepped back out of — see lib/routes.ts. */}
                    <Route path={WORKSPACE_ROUTE} element={<Workspace />} />
                    <Route path={TASK_ROUTE} element={<Workspace />} />
                    <Route path="*" element={<Navigate to={WORKSPACE_ROUTE} />} />
                  </Routes>
                </Suspense>
              </WebSocketProvider>
            </ResponsiveLayoutProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
